import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Gift, Heart, Download, Users, MessageCircle, Globe, Sparkles } from 'lucide-react';
import {
  getPublicGiftBySlug,
  getPublicGiftByToken,
  getCapsuleGiftPdfUrl,
  getCapsuleGiftPdfUrlByToken,
  type MemoryCapsuleGift,
  type GiftMessage,
} from '@/src/infrastructure/lib/api';

// ── Floating particle animation ───────────────────────────────────────────────

function FloatingParticle({ delay, x, emoji }: { delay: number; x: number; emoji: string }) {
  return (
    <motion.div
      className="absolute text-2xl pointer-events-none select-none"
      style={{ left: `${x}%`, bottom: '-10%' }}
      animate={{
        y: [0, -window.innerHeight - 100],
        opacity: [0, 1, 1, 0],
        rotate: [-10, 10, -5, 8],
      }}
      transition={{
        duration: 6 + Math.random() * 4,
        delay,
        repeat: Infinity,
        repeatDelay: Math.random() * 5,
        ease: 'easeOut',
      }}
    >
      {emoji}
    </motion.div>
  );
}

const PARTICLES = ['❤️', '🎉', '✨', '🎈', '💜', '⭐', '🎊', '💝'];

function ParticleField({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-10">
      {Array.from({ length: 16 }, (_, i) => (
        <FloatingParticle
          key={i}
          delay={i * 0.4}
          x={5 + (i * 6.2) % 90}
          emoji={PARTICLES[i % PARTICLES.length]}
        />
      ))}
    </div>
  );
}

// ── Message bubble ─────────────────────────────────────────────────────────────

function MessageBubble({ msg, index, visible }: { msg: GiftMessage; index: number; visible: boolean }) {
  const isLeft = index % 2 === 0;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key={msg.id}
          initial={{ opacity: 0, x: isLeft ? -40 : 40, y: 20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.5, delay: Math.min(index * 0.12, 2.5), ease: [0.22, 1, 0.36, 1] }}
          className={`flex w-full mb-4 ${isLeft ? 'justify-start' : 'justify-end'}`}
        >
          <div
            className={`max-w-[80%] sm:max-w-[65%] rounded-2xl px-4 py-3 shadow-lg ${
              isLeft
                ? 'bg-white/10 border border-white/15 rounded-tl-sm'
                : 'bg-purple-600/30 border border-purple-400/30 rounded-tr-sm'
            }`}
          >
            <p className="text-purple-300 text-xs font-bold mb-1 tracking-wide">
              {msg.senderName}
            </p>
            <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">
              {msg.message}
            </p>
            <p className="text-white/30 text-xs mt-2 text-right">
              {new Date(msg.createdAt).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
              })}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Stats banner ──────────────────────────────────────────────────────────────

function StatsBanner({ gift }: { gift: MemoryCapsuleGift }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-2xl mx-auto mb-8"
    >
      <div className="bg-white/5 border border-white/10 rounded-3xl px-6 py-5 grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-2xl sm:text-3xl font-bold text-purple-300">
            💌 {gift.totalMessages}
          </p>
          <p className="text-white/50 text-xs mt-1">
            Message{gift.totalMessages !== 1 ? 's' : ''}
          </p>
        </div>
        <div>
          <p className="text-2xl sm:text-3xl font-bold text-pink-300">
            ❤️ {gift.totalSenders}
          </p>
          <p className="text-white/50 text-xs mt-1">
            Friend{gift.totalSenders !== 1 ? 's' : ''} &amp; Family
          </p>
        </div>
        <div>
          <p className="text-2xl sm:text-3xl font-bold text-yellow-300">🌎</p>
          <p className="text-white/50 text-xs mt-1">Sent with Love</p>
        </div>
      </div>
      <p className="text-center text-white/25 text-xs mt-2">Generated by InviteCloud</p>
    </motion.div>
  );
}

// ── Intro screen ──────────────────────────────────────────────────────────────

function GiftIntroScreen({
  gift,
  onOpen,
}: {
  gift: MemoryCapsuleGift;
  onOpen: () => void;
}) {
  return (
    <motion.div
      key="intro"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.6 }}
      className="flex flex-col items-center justify-center min-h-screen px-6 text-center"
    >
      {/* Gift box icon */}
      <motion.div
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
        className="w-24 h-24 rounded-3xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-8 shadow-2xl shadow-purple-500/30"
      >
        <Gift size={44} className="text-white" />
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="text-purple-300 text-xs font-bold uppercase tracking-[0.3em] mb-3"
      >
        🎁 A Special Gift Awaits
      </motion.p>

      <motion.h1
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.6 }}
        className="text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight"
      >
        {gift.title}
      </motion.h1>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.65, duration: 0.5 }}
        className="text-white/60 text-base mb-2 max-w-md"
      >
        {gift.subtitle}
      </motion.p>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.75, duration: 0.5 }}
        className="text-lg text-white/40 mb-10"
      >
        Created by{' '}
        <span className="text-purple-300 font-semibold">Friends &amp; Family</span>
        {' '}for{' '}
        <span className="text-white font-bold">{gift.recipientName}</span>
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.5 }}
        className="flex flex-col items-center gap-4"
      >
        {/* Stats preview */}
        <div className="flex items-center gap-6 text-white/40 text-sm mb-4">
          <span className="flex items-center gap-1.5">
            <MessageCircle size={14} className="text-purple-400" />
            {gift.totalMessages} messages
          </span>
          <span className="flex items-center gap-1.5">
            <Users size={14} className="text-pink-400" />
            {gift.totalSenders} people
          </span>
        </div>

        <motion.button
          onClick={onOpen}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          className="px-10 py-4 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-600 text-white font-bold text-lg rounded-2xl shadow-2xl shadow-purple-500/40 hover:shadow-purple-500/60 transition-shadow"
        >
          Open Gift ✨
        </motion.button>

        <p className="text-white/20 text-xs mt-2">
          Collected with love from everyone who wanted to celebrate this special day.
        </p>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 0.5 }}
        className="absolute bottom-6 text-white/20 text-xs"
      >
        Powered by InviteCloud
      </motion.p>
    </motion.div>
  );
}

// ── Messages feed screen ───────────────────────────────────────────────────────

function GiftMessagesFeed({
  gift,
  pdfUrl,
}: {
  gift: MemoryCapsuleGift;
  pdfUrl: string;
}) {
  const [showAll, setShowAll] = useState(false);
  const feedRef = useRef<HTMLDivElement>(null);

  const visible = showAll ? gift.messages : gift.messages.slice(0, 20);

  return (
    <motion.div
      key="feed"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center min-h-screen px-4 pt-8 pb-20"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-2xl mx-auto text-center mb-8"
      >
        <div className="inline-flex items-center gap-2 bg-purple-500/20 border border-purple-400/30 rounded-full px-4 py-1.5 mb-4">
          <Sparkles size={12} className="text-purple-300" />
          <span className="text-purple-300 text-xs font-bold uppercase tracking-widest">
            Memory Gift
          </span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1">
          {gift.title}
        </h2>
        <p className="text-white/40 text-sm">For {gift.recipientName}</p>
      </motion.div>

      {/* Stats banner */}
      <StatsBanner gift={gift} />

      {/* Chat-style message feed */}
      <div
        ref={feedRef}
        className="w-full max-w-2xl mx-auto flex flex-col"
      >
        {visible.map((msg, i) => (
          <MessageBubble key={msg.id} msg={msg} index={i} visible />
        ))}

        {!showAll && gift.messages.length > 20 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2 }}
            className="text-center mt-4 mb-2"
          >
            <button
              onClick={() => setShowAll(true)}
              className="text-purple-300 text-sm underline underline-offset-2 hover:text-purple-200 transition-colors"
            >
              Show all {gift.messages.length} messages
            </button>
          </motion.div>
        )}
      </div>

      {/* PDF export + footer */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.5 }}
        className="w-full max-w-2xl mx-auto mt-10 text-center"
      >
        <div className="bg-white/5 border border-white/10 rounded-3xl px-6 py-6">
          <p className="text-white/50 text-sm mb-4">
            Keep these memories forever
          </p>
          <a
            href={pdfUrl}
            download
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white text-sm font-semibold rounded-xl hover:bg-white/15 transition-colors"
          >
            <Download size={15} />
            Download as PDF Keepsake
          </a>
          <p className="text-white/20 text-xs mt-4">
            Powered by InviteCloud · invitecloud.in
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

type GiftPhase = 'loading' | 'intro' | 'open' | 'error';

export function GiftExperiencePage() {
  const { giftSlug } = useParams<{ giftSlug: string }>();
  const [searchParams] = useSearchParams();
  const shareToken = searchParams.get('token');

  const [gift, setGift] = useState<MemoryCapsuleGift | null>(null);
  const [phase, setPhase] = useState<GiftPhase>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        let data: MemoryCapsuleGift;
        if (shareToken) {
          data = await getPublicGiftByToken(shareToken);
        } else if (giftSlug) {
          data = await getPublicGiftBySlug(giftSlug);
        } else {
          throw new Error('No gift identifier provided');
        }
        setGift(data);
        setPhase('intro');
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : 'Could not load this gift.');
        setPhase('error');
      }
    };
    load();
  }, [giftSlug, shareToken]);

  const pdfUrl = shareToken
    ? getCapsuleGiftPdfUrlByToken(shareToken)
    : gift
    ? getCapsuleGiftPdfUrl(gift.giftSlug)
    : '#';

  return (
    <div
      className="relative min-h-screen overflow-hidden"
      style={{
        background: 'radial-gradient(ellipse at 50% 0%, #1a0a3c 0%, #0f0a1e 40%, #060412 100%)',
        fontFamily: '"Inter", system-ui, sans-serif',
      }}
    >
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-purple-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[300px] bg-pink-600/8 rounded-full blur-3xl" />
      </div>

      {/* Particles — only when gift is open */}
      <ParticleField active={phase === 'open'} />

      <div className="relative z-20">
        <AnimatePresence mode="wait">
          {phase === 'loading' && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center min-h-screen gap-4"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                className="w-12 h-12 rounded-full border-2 border-purple-400/30 border-t-purple-400"
              />
              <p className="text-white/40 text-sm">Opening your gift…</p>
            </motion.div>
          )}

          {phase === 'intro' && gift && (
            <GiftIntroScreen
              key="intro"
              gift={gift}
              onOpen={() => setPhase('open')}
            />
          )}

          {phase === 'open' && gift && (
            <GiftMessagesFeed
              key="feed"
              gift={gift}
              pdfUrl={pdfUrl}
            />
          )}

          {phase === 'error' && (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center min-h-screen gap-4 px-6 text-center"
            >
              <div className="text-5xl mb-2">🔒</div>
              <h2 className="text-white text-xl font-bold">Gift Not Found</h2>
              <p className="text-white/40 text-sm max-w-xs">{errorMsg}</p>
              <a
                href="/"
                className="mt-4 text-purple-300 text-sm underline underline-offset-2"
              >
                Go to InviteCloud
              </a>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
